<template>
  <div class="card card-body">

    <!-- Download Template -->
    <button class="btn btn-outline-secondary mb-2" @click="downloadSample"
      :disabled="isDownloading || !loanTypes.length">
      <span v-if="isDownloading" class="spinner-border spinner-border-sm me-2"></span>
      {{ isDownloading ? 'Preparing...' : 'Download Loan Template' }}
    </button>

    <div v-if="isDownloading" class="mb-3">
      <div class="progress">
        <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: downloadProgress + '%' }">
          {{ downloadProgress }}%
        </div>
      </div>
    </div>

    <input type="file" accept=".xlsx,.xls" class="form-control mb-3" @change="handleFile" />

    <input v-if="rows.length" class="form-control mb-2" placeholder="Search..." v-model="search" />

    <div v-if="isImporting" class="mb-3">
      <div class="progress">
        <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: progress + '%' }">
          {{ progress }}%
        </div>
      </div>
      <small class="text-muted">
        Processed {{ processedRows }} of {{ totalRows }} rows
      </small>
    </div>

    <div class="table-responsive" style="max-height:420px">
      <div class="mb-3">
        <label class="form-label">Disbursement Date:</label>
        <input type="date" class="form-control" v-model="batchDisbursementDate" />
      </div>
      <table class="table table-sm table-bordered">
        <thead class="table-light">
          <tr>
            <th>#</th>
            <th>Client Name</th>
            <th>Client ID Number</th>
            <th>Loan Type</th>
            <th>Principal</th>
            <th>Term</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(r, i) in filteredRows" :key="r._rowIndex" :class="rowClass(r)">
            <td>{{ i + 1 }}</td>
            <td>{{ r.clientName }}</td>
            <td>{{ r.clientIdNumber }}</td>

            <td>
              <select class="form-select form-select-sm" v-model="r.loanTypeName" @change="validateRow(r)">
                <option value="">Select</option>
                <option v-for="lt in loanTypes" :key="lt.id" :value="lt.name">
                  {{ lt.name }}
                </option>
              </select>
            </td>

            <td>
              <input type="number" class="form-control form-control-sm" v-model.number="r.principalAmount"
                @input="validateRow(r)" />
            </td>

            <td>
              <input type="number" class="form-control form-control-sm" v-model.number="r.loanTerm"
                @input="validateRow(r)" />
            </td>

            <td>
              <span v-if="r._status === 'SUCCESS'" class="text-success">✔ {{ r._remark }}</span>
              <span v-else-if="r._status === 'FAILED'" class="text-danger">✖ {{ r._remark }}</span>
              <span v-else class="text-muted">Pending</span>
            </td>

            <td>
              <button class="btn btn-sm btn-danger" @click="deleteRow(i)">✖</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button class="btn btn-success mt-3" @click="submitRows">Submit Loans</button>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import loanTypeApi from '@/api/loanTypeApi'
import clientApi from '@/api/clientApi'
import loanApi from '@/api/loanApi'

export default {
  name: 'BatchLoanCreation',

  data() {
    return {
      rows: [],
      search: '',
      loanTypes: [],
      isDownloading: false,
      downloadProgress: 0,
      isImporting: false,
      progress: 0,
      processedRows: 0,
      totalRows: 0
    }
  },

  computed: {
    filteredRows() {
      if (!this.search) return this.rows
      return this.rows.filter(r =>
        Object.values(r).join(' ').toLowerCase().includes(this.search.toLowerCase())
      )
    }
  },

  async mounted() {
    const res = await loanTypeApi.getAll()
    this.loanTypes = res.data
  },

  methods: {

    /* =========================
     * DOWNLOAD TEMPLATE
     * ========================= */
    async downloadSample() {
      const wb = new ExcelJS.Workbook()
      const sheet = wb.addWorksheet('BatchLoans')

      sheet.columns = [
        { header: 'Client ID', key: 'clientId', hidden: true },
        { header: 'Client Name', key: 'clientName', width: 30 },
        { header: 'Client ID Number', key: 'clientIdNumber', width: 20 },
        { header: 'Loan Type', key: 'loanTypeName', width: 25 },
        { header: 'Principal Amount', key: 'principalAmount', width: 15 },
        { header: 'Loan Term (Months)', key: 'loanTerm', width: 18 }
      ]

      sheet.getRow(1).font = { bold: true }
      sheet.views = [{ state: 'frozen', ySplit: 1 }]

      const clients = (await clientApi.getAll()).data
      clients.forEach(c => {
        sheet.addRow({
          clientId: c.id,
          clientName: c.fullName,
          clientIdNumber: c.idNumber,
          principalAmount: 0,
          loanTerm: 12
        })
      })

      const lookup = wb.addWorksheet('LOOKUPS', { state: 'hidden' })
      lookup.getColumn(1).values = ['LOAN_TYPES', ...this.loanTypes.map(l => l.name)]

      sheet.dataValidations.add(
        `D2:D${clients.length + 1}`,
        {
          type: 'list',
          allowBlank: true,
          formulae: [`LOOKUPS!$A$2:$A$${this.loanTypes.length + 1}`],
          showErrorMessage: true,
          errorTitle: 'Invalid Loan Type',
          error: 'Please select a loan type from the dropdown'
        }
      )

      const buffer = await wb.xlsx.writeBuffer()
      saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        'batch_loan_template.xlsx'
      )
    },

    /* =========================
     * HANDLE FILE UPLOAD
     * ========================= */
    handleFile(e) {
      const reader = new FileReader()
      reader.onload = ev => {
        const wb = XLSX.read(ev.target.result, { type: 'array' })
        const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })

        this.rows = data.map((r, i) => ({
          clientId: Number(r['Client ID']),
          clientName: r['Client Name'],
          clientIdNumber: r['Client ID Number'],
          loanTypeName: r['Loan Type'],
          principalAmount: Number(r['Principal Amount']) || 0,
          loanTerm: Number(r['Loan Term (Months)']) || 12,
          _rowIndex: i,
          _errors: {},
          _status: 'PENDING',
          _remark: ''
        }))

        this.rows.forEach(this.validateRow)
      }
      reader.readAsArrayBuffer(e.target.files[0])
    },

    validateRow(r) {
      r._errors = {}
      if (!this.loanTypes.find(l => l.name === r.loanTypeName))
        r._errors.loanType = true
    },

    rowClass(r) {
      if (r._status === 'SUCCESS') return 'table-success'
      if (r._status === 'FAILED') return 'table-danger'
      if (Object.keys(r._errors).length) return 'table-warning'
      return ''
    },

    deleteRow(i) {
      this.rows.splice(i, 1)
    },

    /* =========================
     * SUBMIT ROWS
     * ========================= */
    async submitRows() {
      const validRows = this.rows
        .map((r, index) => ({ r, index }))
        .filter(x => Object.keys(x.r._errors).length === 0)

      if (!validRows.length) return alert('No valid rows to submit')

      this.isImporting = true
      this.processedRows = 0
      this.totalRows = validRows.length

      // Optional: submit in chunks for large batches
      const chunkSize = 20
      for (let i = 0; i < validRows.length; i += chunkSize) {
        const chunk = validRows.slice(i, i + chunkSize)
        try {
          const res = await loanApi.createBatchLoans({
            loans: chunk.map(x => ({
              clientId: x.r.clientId,
              loanTypeId: this.loanTypes.find(l => l.name === x.r.loanTypeName)?.id,
              principalAmount: x.r.principalAmount,
              loanTerm: x.r.loanTerm,
              creationDate: this.batchDisbursementDate, // same date for all
              startDate: this.batchDisbursementDate, // same date for all
              index: x.index // include index for mapping results
            })),
            autoDisburse: true
          })

          res.data.forEach(r => {
            const row = this.rows[r.index]
            if (row) {
              row._status = r.status
              row._remark = r.message
            }
          })
        } catch (err) {
          chunk.forEach(x => {
            x.r._status = 'FAILED'
            x.r._remark = 'System error'
          })
        }

        this.processedRows += chunk.length
        this.progress = Math.round((this.processedRows / this.totalRows) * 100)
      }

      this.isImporting = false
    }

  }
}
</script>
